// src/pages/Account.jsx
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronRight,
  FaSignOutAlt,
  FaUserCircle,
  FaHeadset,
  FaShieldAlt,
  FaBus,
  FaTools,
  FaArrowLeft,
  FaTicketAlt,
  FaUserEdit,
} from "react-icons/fa";

// ✅ Use the same Auth hook your app uses
import { useAuth } from "../context/AuthContext";

const PALETTE = {
  primary: "#D84E55",
  bg: "#F5F6F8",
  white: "#FFFFFF",
  textDark: "#111827",
  textLight: "#6B7280",
  borderLight: "rgba(15,23,42,0.08)",
  redSoft: "rgba(216,78,85,0.08)",
  redSoftBorder: "rgba(216,78,85,0.14)",
};

const SectionTitle = ({ children }) => (
  <p
    className="text-[11px] font-bold uppercase tracking-[0.16em] mb-2"
    style={{ color: PALETTE.textLight }}
  >
    {children}
  </p>
);

const IconBadge = ({ icon: Icon, danger = false }) => (
  <div
    className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0"
    style={{
      backgroundColor: PALETTE.redSoft,
      border: `1px solid ${danger ? "rgba(220,38,38,0.12)" : PALETTE.redSoftBorder}`,
    }}
  >
    {Icon ? (
      <Icon
        className="text-[16px]"
        style={{ color: danger ? "#dc2626" : PALETTE.primary }}
      />
    ) : null}
  </div>
);

const RowLink = ({ to, icon: Icon, label, subLabel, highlight = false }) => (
  <Link
    to={to}
    className="w-full flex items-center justify-between px-4 py-3.5 rounded-[20px] bg-white active:scale-[0.99] transition-all duration-200"
    style={{
      border: `1px solid ${
        highlight ? PALETTE.redSoftBorder : PALETTE.borderLight
      }`,
      boxShadow: "0 10px 24px -20px rgba(15,23,42,0.20)",
    }}
  >
    <div className="flex items-center gap-3 min-w-0">
      <IconBadge icon={Icon} />
      <div className="min-w-0">
        <p
          className="text-[14px] font-semibold truncate"
          style={{ color: PALETTE.textDark }}
        >
          {label}
        </p>
        {subLabel ? (
          <p
            className="text-[11px] mt-0.5 truncate"
            style={{ color: PALETTE.textLight }}
          >
            {subLabel}
          </p>
        ) : null}
      </div>
    </div>

    <div className="flex items-center gap-2 shrink-0">
      {highlight ? (
        <span
          className="text-[10px] font-bold px-2 py-1 rounded-full"
          style={{
            backgroundColor: PALETTE.redSoft,
            color: PALETTE.primary,
          }}
        >
          Quick
        </span>
      ) : null}
      <FaChevronRight className="text-[13px]" style={{ color: "#9CA3AF" }} />
    </div>
  </Link>
);

const RowButton = ({ onClick, icon: Icon, label, subLabel, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left flex items-center justify-between px-4 py-3.5 rounded-[20px] bg-white active:scale-[0.99] transition-all duration-200"
    style={{
      border: `1px solid ${
        danger ? "rgba(220,38,38,0.12)" : PALETTE.borderLight
      }`,
      boxShadow: "0 10px 24px -20px rgba(15,23,42,0.20)",
    }}
  >
    <div className="flex items-center gap-3 min-w-0">
      <IconBadge icon={Icon} danger={danger} />
      <div className="min-w-0">
        <p
          className="text-[14px] font-semibold truncate"
          style={{ color: danger ? "#dc2626" : PALETTE.textDark }}
        >
          {label}
        </p>
        {subLabel ? (
          <p
            className="text-[11px] mt-0.5 truncate"
            style={{ color: PALETTE.textLight }}
          >
            {subLabel}
          </p>
        ) : null}
      </div>
    </div>

    {danger ? (
      <span className="text-[12px] font-semibold text-red-600">Exit</span>
    ) : (
      <FaChevronRight className="text-[13px]" style={{ color: "#9CA3AF" }} />
    )}
  </button>
);

export default function Account() {
  const navigate = useNavigate();
  const auth = useAuth() || {};

  const { user, token, loading, logout = () => {} } = auth;

  // ✅ Keep login logic aligned with MobileBottomNav
  const isLoggedIn = !!user && !!token && !loading;

  const role = user?.role || "user";
  const name = user?.name || "User";
  const email = user?.email || "";

  const isOperator = role === "operator";
  const isAdmin = role === "admin";

  // ✅ Keep mobile-only behavior
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    if (mq.matches) navigate("/profile", { replace: true });
  }, [navigate]);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div
      className="lg:hidden min-h-screen"
      style={{ backgroundColor: PALETTE.bg }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-20 bg-white"
        style={{ borderBottom: `1px solid ${PALETTE.borderLight}` }}
      >
        <div className="px-4 pt-3 pb-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-full flex items-center justify-center bg-white"
            style={{ border: `1px solid ${PALETTE.borderLight}` }}
            aria-label="Go back"
          >
            <FaArrowLeft className="text-[13px]" style={{ color: PALETTE.textLight }} />
          </button>

          <div className="text-center">
            <p
              className="text-[14px] font-semibold"
              style={{ color: PALETTE.textDark }}
            >
              Account
            </p>
            <p
              className="text-[10px] -mt-0.5"
              style={{ color: PALETTE.textLight }}
            >
              Your travel hub
            </p>
          </div>

          <div
            className="h-6 min-w-[24px] px-1.5 rounded-full flex items-center justify-center text-[9px] font-bold"
            style={{
              backgroundColor: PALETTE.redSoft,
              color: PALETTE.primary,
            }}
          >
            RB
          </div>
        </div>
      </div>

      {/* Red strip above user card */}
      <div style={{ backgroundColor: PALETTE.primary, height: 64 }} />

      {/* User card */}
      <div className="px-4 -mt-8 pt-0">
        <div
          className="rounded-[24px] p-4 bg-white"
          style={{
            border: `1px solid ${PALETTE.redSoftBorder}`,
            boxShadow: "0 18px 34px -26px rgba(216,78,85,0.28)",
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center shrink-0"
              style={{
                backgroundColor: PALETTE.redSoft,
                border: `1px solid ${PALETTE.redSoftBorder}`,
              }}
            >
              <FaUserCircle
                className="text-[30px]"
                style={{ color: PALETTE.primary }}
              />
            </div>

            <div className="min-w-0 flex-1">
              <p
                className="text-[16px] font-semibold truncate"
                style={{ color: PALETTE.textDark }}
              >
                {isLoggedIn ? name : "Welcome"}
              </p>
              <p
                className="text-[11px] mt-0.5 truncate"
                style={{ color: PALETTE.textLight }}
              >
                {isLoggedIn
                  ? email || "You are signed in"
                  : "Sign in to manage bookings faster"}
              </p>

              {isLoggedIn ? (
                <span
                  className="mt-2 inline-flex text-[10px] px-2.5 py-1 rounded-full font-semibold capitalize"
                  style={{
                    backgroundColor: PALETTE.redSoft,
                    color: PALETTE.primary,
                  }}
                >
                  {role}
                </span>
              ) : null}
            </div>
          </div>

          {!loading && !isLoggedIn ? (
            <div className="mt-4">
              <Link
                to="/login"
                className="block w-full text-center rounded-full py-3 text-white font-semibold"
                style={{ backgroundColor: PALETTE.primary }}
              >
                Log in
              </Link>

              <p
                className="text-[11px] text-center mt-2"
                style={{ color: PALETTE.textLight }}
              >
                Don’t have an account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold"
                  style={{ color: PALETTE.primary }}
                >
                  Sign up
                </Link>
              </p>
            </div>
          ) : null}

          {isLoggedIn ? (
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              <div
                className="rounded-[18px] px-3 py-2.5"
                style={{
                  backgroundColor: "#FAFAFA",
                  border: `1px solid ${PALETTE.borderLight}`,
                }}
              >
                <p
                  className="text-[10px] font-medium"
                  style={{ color: PALETTE.textLight }}
                >
                  Account status
                </p>
                <p
                  className="text-[13px] font-semibold mt-0.5"
                  style={{ color: PALETTE.textDark }}
                >
                  Active
                </p>
              </div>

              <div
                className="rounded-[18px] px-3 py-2.5"
                style={{
                  backgroundColor: "#FAFAFA",
                  border: `1px solid ${PALETTE.borderLight}`,
                }}
              >
                <p
                  className="text-[10px] font-medium"
                  style={{ color: PALETTE.textLight }}
                >
                  Access
                </p>
                <p
                  className="text-[13px] font-semibold mt-0.5 capitalize"
                  style={{ color: PALETTE.textDark }}
                >
                  {role}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Sections */}
      <div className="px-4 pt-5 pb-4 space-y-5">
        <section>
          <SectionTitle>My details</SectionTitle>
          <div className="space-y-2.5">
            <RowLink
              to="/my-bookings"
              icon={FaTicketAlt}
              label="My Bookings"
              subLabel="View upcoming and past trips"
              highlight
            />
            <RowLink
              to="/profile"
              icon={FaUserEdit}
              label="Personal information"
              subLabel="Update your profile details"
            />
          </div>
        </section>

        <section>
          <SectionTitle>Support</SectionTitle>
          <div className="space-y-2.5">
            <RowLink
              to="/help"
              icon={FaHeadset}
              label="Help Center"
              subLabel="FAQs and support"
            />
            <RowLink
              to="/terms"
              icon={FaShieldAlt}
              label="Terms & Policies"
              subLabel="Privacy, terms, and refunds"
            />
          </div>
        </section>

        {isLoggedIn && (isOperator || isAdmin) ? (
          <section>
            <SectionTitle>{isAdmin ? "Admin" : "Operator"}</SectionTitle>
            <div className="space-y-2.5">
              {isOperator ? (
                <RowLink
                  to="/operator/dashboard"
                  icon={FaBus}
                  label="Operator Dashboard"
                  subLabel="Manage buses and bookings"
                />
              ) : null}

              {isAdmin ? (
                <RowLink
                  to="/admin"
                  icon={FaTools}
                  label="Admin Dashboard"
                  subLabel="Manage the platform"
                />
              ) : null}
            </div>
          </section>
        ) : null}

        {isLoggedIn ? (
          <section>
            <SectionTitle>Actions</SectionTitle>
            <div className="space-y-2.5">
              <RowButton
                onClick={handleLogout}
                icon={FaSignOutAlt}
                label="Logout"
                subLabel="Sign out from this device"
                danger
              />
            </div>
          </section>
        ) : null}
      </div>

      {/* Bottom nav space */}
      <div
        style={{
          height: 96,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      />
    </div>
  );
}
